import { useState } from 'react';
import { PanelLeft, Palette, ChevronDown, Sun, Moon, Lock, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useSidebar } from '@/hooks/useSidebar';
import { useTabs } from '@/hooks/useTabs';
import { useAuth } from '@/hooks/useAuth';
import { FeedbackFlag } from '@/components/feedback/FeedbackFlag';
import { useGameDayStatus } from '@/hooks/useGameDayStatus';
import { useClientOptions } from '@/components/client-options';
import { useTheme, type ThemeId } from '@/app/providers/ThemeProvider';
import { AboutDialog } from './AboutDialog';
import { Logo } from '@/components/brand/Logo';
import { useMenuConfig } from '@/hooks/useMenuConfig';
import { openSystemOptionsTab } from '@/tools/SystemOptions/openSystemOptions';
import { REFRESHING_SECURITY_LABEL, REFRESH_SECURITY_LABEL } from '@/tools/SecurityControls/copy';
import { useRefreshSecurity } from '@/tools/SecurityControls/refreshSecurity';
import { useSystemOptionsPermission } from '@/tools/SystemOptions/usePermission';
import type { ToolManifest } from '@ic/platform-contract';

// ─── Exported helpers (unit-tested by T-5, T-6, T-14) ─────────────────────

export function getInitials(displayName: string): string {
  return displayName
    .split(/\s+/)
    .map(w => w[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export interface BreadcrumbResult {
  desktop: string | undefined;
  tool: string;
}

export function resolveBreadcrumb(
  manifest: ToolManifest | null,
  desktops: Array<{ id: string; label: string }>,
): BreadcrumbResult | null {
  if (!manifest) return null;
  const menuEntry = manifest.menuEntry;
  const desktopEntry = menuEntry ? desktops.find(d => d.id === menuEntry.desktop) : undefined;
  return { desktop: desktopEntry?.label, tool: manifest.label };
}

// ─── Avatar color ──────────────────────────────────────────────────────────

// A hash-assigned avatar needs several concurrently-visible colors, not the single active
// --primary value a var() reference exposes. The design system's categorical --chart-1..6
// tokens are the intended reuse target for exactly this kind of case, but globals.css's
// current --chart-* values are still a grayscale placeholder ramp (oklch lightness steps),
// not yet the Blue/Amber/Teal/Purple/Rose/Slate set design_system/readme.md describes —
// pointing avatarColor() at them today would turn every avatar a shade of gray, a real
// regression, not a fix. Literal hex stays until that token gap is closed upstream.
// eslint-disable-next-line no-restricted-syntax -- see comment above (design-system-redesigns/app-shell-sync-back.md)
const AVATAR_PALETTE = ['#2E6E5E', '#3B5E8A', '#7A4B2A', '#5A3E6B', '#2A5E4A'] as const;

function avatarColor(displayName: string): string {
  const idx = [...displayName].reduce((a, c) => a + (c.codePointAt(0) ?? 0), 0) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx] ?? AVATAR_PALETTE[0];
}

// ─── ThemePicker ──────────────────────────────────────────────────────────

// Each swatch previews a palette that ISN'T necessarily the active one, so these can't be
// var(--primary) references (that only ever resolves the current palette) — the picker needs
// every option's color visible concurrently. Literal hex is the correct representation here,
// not a gap.
/* eslint-disable no-restricted-syntax -- swatch previews for inactive palettes can't be var() references (design-system-redesigns/app-shell-sync-back.md) */
const THEME_SWATCH: Readonly<Record<ThemeId, string>> = {
  green: '#006F62',
  blue: '#1A4C94',
  purple: '#52377B',
  // slate/bronze/berry/contrast were placeholder approximations until the
  // real Design System values shipped post-delivery (2026-08-25) — now
  // match globals.css's data-palette blocks exactly (tokens/product.css).
  slate: '#475569',
  bronze: '#7A5C3E',
  berry: '#8E3B6B',
  contrast: '#000000',
};
/* eslint-enable no-restricted-syntax */

function ThemePicker() {
  const { theme, themes, themeLabel, setTheme } = useTheme();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Select theme"
          data-testid="theme-picker-trigger"
          className="inline-flex items-center justify-center rounded p-1.5 hover:bg-white/10 focus:outline-none"
        >
          <Palette className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="z-50 min-w-40 rounded border border-border bg-background p-1 shadow-md"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuLabel className="px-2 py-1 text-xs font-semibold text-muted-foreground">
          Themes
        </DropdownMenuLabel>
        {themes.map((id) => (
          <DropdownMenuItem
            key={id}
            onSelect={() => setTheme(id)}
            data-testid={`theme-option-${id}`}
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none hover:bg-muted focus:bg-muted"
          >
            <span
              aria-hidden
              className="inline-block size-3 rounded-full border border-border/50"
              style={{ background: THEME_SWATCH[id] }}
            />
            <span>{themeLabel(id)}</span>
            {theme === id && (
              <span className="ml-auto text-muted-foreground" aria-label="(active)">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── ModeToggle ────────────────────────────────────────────────────────────

function ModeToggle() {
  const { mode, setMode } = useTheme();

  return (
    <button
      onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
      aria-label="Toggle light/dark mode"
      data-testid="mode-toggle"
      className="inline-flex items-center justify-center rounded p-1.5 hover:bg-white/10 focus:outline-none"
    >
      {mode === 'dark' ? <Moon className="size-4" /> : <Sun className="size-4" />}
    </button>
  );
}

// ─── UserPill ──────────────────────────────────────────────────────────────

function UserPill() {
  const auth = useAuth();
  const clientOptions = useClientOptions();
  const { openTab } = useTabs();
  const canOpenSystemOptions = useSystemOptionsPermission();
  // E-111: called here, not in the menu content, so an in-flight refresh survives the menu closing.
  const { refresh: refreshSecurity, isRefreshing: isRefreshingSecurity } = useRefreshSecurity();
  const isSystemAdmin = auth.effectivePermissions?.isSystemAdmin === true;
  const [aboutOpen, setAboutOpen] = useState(false);
  const displayName = auth.user?.displayName ?? 'User';
  const initials = getInitials(displayName);
  const bg = avatarColor(displayName);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 rounded px-2 py-1 hover:bg-white/10 focus:outline-none"
            aria-label={`User menu for ${displayName}`}
          >
            <span
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
              style={{ background: bg }}
              aria-hidden
            >
              {initials}
            </span>
            <span className="max-w-32 truncate text-sm">{displayName}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="z-50 min-w-44 rounded border border-border bg-background p-1 shadow-md"
          side="bottom"
          align="end"
          sideOffset={4}
        >
          {/* D-001/D-002: Client Options… and System Options… (legacy-verbatim labels) share one
              section of the profile menu. System Options… opens its own tab, never a dialog
              (D-004), and only for users holding Maintenance\System Options. */}
          <DropdownMenuItem
            className="cursor-pointer rounded px-2 py-1.5 text-sm outline-none hover:bg-muted focus:bg-muted"
            onSelect={() => clientOptions.launch()}
          >
            Client Options…
          </DropdownMenuItem>
          {canOpenSystemOptions && (
            <DropdownMenuItem
              className="cursor-pointer rounded px-2 py-1.5 text-sm outline-none hover:bg-muted focus:bg-muted"
              onSelect={() => openSystemOptionsTab(openTab)}
              data-testid="system-options-menu-item"
            >
              System Options…
            </DropdownMenuItem>
          )}
          {/* E-100/E-101: absent, not disabled, for anyone but a System Administrator. The server
              stays authoritative (403). Selecting runs at once: a refresh changes no grant. */}
          {isSystemAdmin && (
            <DropdownMenuItem
              className="cursor-pointer rounded px-2 py-1.5 text-sm outline-none hover:bg-muted focus:bg-muted"
              disabled={isRefreshingSecurity}
              onSelect={() => refreshSecurity()}
              data-testid="refresh-security-menu-item"
            >
              {isRefreshingSecurity ? REFRESHING_SECURITY_LABEL : REFRESH_SECURITY_LABEL}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer rounded px-2 py-1.5 text-sm outline-none hover:bg-muted focus:bg-muted"
            onSelect={() => setAboutOpen(true)}
          >
            About Insight Cash
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer rounded px-2 py-1.5 text-sm outline-none hover:bg-muted focus:bg-muted"
            onSelect={() => auth.logout()}
          >
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </>
  );
}

// ─── TopBar ────────────────────────────────────────────────────────────────

export const TOPBAR_HEIGHT = 54;

export function TopBar() {
  const sidebar = useSidebar();
  const { activeManifest } = useTabs();
  // B-7 — the same desktops the sidebar renders, from the same source. This
  // read the build-time `data/desktops.json` until 2026-09-12, which meant a
  // desktop renamed in the seeded config showed one name in the sidebar and
  // another here. `useMenuConfig` is cached with `staleTime: Infinity`, so
  // this is the sidebar's already-resolved query, not a second fetch.
  //
  // Before it resolves (cold start, or an unauthenticated render) `desktops`
  // is empty, `resolveBreadcrumb` finds no match and returns
  // `desktop: undefined`, and the nav below already renders the tool label
  // alone in that case — the degradation is one crumb, briefly, not a
  // missing breadcrumb.
  const { data: menuConfig } = useMenuConfig();
  const breadcrumb = resolveBreadcrumb(activeManifest, menuConfig?.desktops ?? []);
  const { isLocked, gameDay } = useGameDayStatus();

  return (
    <TooltipProvider delayDuration={600}>
      <header
        style={{ height: TOPBAR_HEIGHT }}
        className="flex shrink-0 items-center gap-2 bg-topbar px-3 text-topbar-fg"
        data-topbar
      >
        {/* Zone 1 — Sidebar toggle */}
        <button
          onClick={() => sidebar.toggle()}
          aria-label="Toggle sidebar"
          className="inline-flex items-center justify-center rounded p-1.5 hover:bg-white/10 focus:outline-none"
        >
          <PanelLeft className="size-5" />
        </button>

        {/* Zone 2 — Product switcher stub */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 rounded px-2 py-1 hover:bg-white/10 focus:outline-none">
              <Logo className="size-5" />
              <span className="shell-brand-title">Insight Cash</span>
              <ChevronDown className="size-3 opacity-60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="z-50 min-w-48 rounded border border-border bg-background p-1 shadow-md"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            <DropdownMenuItem className="rounded px-2 py-1.5 text-sm font-medium outline-none">
              Insight Cash <span className="text-xs opacity-60">(current)</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled
              className="cursor-not-allowed rounded px-2 py-1.5 text-sm opacity-40 outline-none"
            >
              Insight Analytics
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* topbar-sep — 1px × 22px white/15% separator (prototype §topbar-sep) */}
        <div className="mx-1 h-[calc(--spacing(5.5))] w-px bg-white/15 shrink-0" aria-hidden />

        {/* Zone 3 — Breadcrumb */}
        {breadcrumb ? (
          <nav
            aria-label="Breadcrumb"
            className="flex min-w-0 flex-1 items-center gap-1 text-sm opacity-80"
          >
            {breadcrumb.desktop !== undefined && (
              <>
                <span className="truncate">{breadcrumb.desktop}</span>
                <span aria-hidden className="opacity-50">
                  ›
                </span>
              </>
            )}
            <span className="truncate font-medium opacity-100">{breadcrumb.tool}</span>
          </nav>
        ) : (
          <div className="flex-1" />
        )}

        {/* D-009: game-day lock renders as a top-bar chip beside the breadcrumb, not a
            full-width banner (a banner spanned the sidebar it didn't affect, pushed every
            screen down permanently, and its dismiss destroyed the information). */}
        {isLocked && (
          <span
            className="flex shrink-0 items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-xs"
            title={gameDay ? `Game day ${gameDay.gameDay} is locked` : 'Game day locked'}
          >
            <Lock className="size-3" />
            Game Day Locked
          </span>
        )}

        {/* Zone 4 — Right cluster */}
        <div className="flex items-center gap-1">
          <ThemePicker />

          <ModeToggle />

          <FeedbackFlag />

          <UserPill />
        </div>
      </header>
    </TooltipProvider>
  );
}
